import React, { useMemo, useRef, useState } from 'react';

import { AvatarInput } from '@components/admin/AvatarInput';
import { FormTitle } from '@components/admin/FormTitle';
import { Layout } from '@components/admin/Layout';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { TextInput } from '@components/Forms/TextInput';
import { HorizontalSpace } from '@components/HorizontalSpace';
import { SpinnerModal } from '@components/SpinnerModal';
import { VerticalSpace } from '@components/VerticalSpace';
import { usePatchStaff } from '@hooks/mutation/dashboard/usePatchStaff';
import { useStaffDelete } from '@hooks/mutation/dashboard/useStaffDelete';
import { useAgencyList } from '@hooks/queries/dashboard/useAgencyList';
import { useStaff } from '@hooks/queries/dashboard/useStaff';
import { useSearch } from '@hooks/useSearch';
import {
  mapRoleOptionToAgencyId,
  mapRoleOptionToRole,
  mapRoleToRoleOptionValue,
  getRoleOptions,
} from '@models/Role';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createValidate, requiredStringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getBreadcrumb, PageMeta, url } from '@utils/pages';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import { useHistory } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: (name: string) => `${name} のプロフィール`,
    employeeId: '社員ID',
    lastName: '姓',
    firstName: '名',
    email: 'メール',
    update: '更新',
    delete: '削除',
    role: '権限グループ',
    updateSuccess: (name: string) => `${name} のプロフィール更新成功`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  backToList: {
    ...common.middleText,
    ...common.fontWeight500,
    color: theme.palette.grey[700],
    marginLeft: 9,
    cursor: 'pointer',
  },
  formBodyContainer: {
    ...common.flexColumn,
    alignItems: 'center',
    paddingTop: '2rem',
    flexGrow: 1,
    rowGap: '20px',
  },
  formBody: {
    ...common.flexColumn,
    alignItems: 'left',
    rowGap: '20px',
  },
  label: {
    ...common.middleText,
    color: theme.palette.grey[800],
  },
  input: {
    ...common.middleText,
  },
  textInput: {
    borderColor: theme.palette.grey[400],
    width: 300,
  },
  rowContainer: {
    ...common.flexCenter,
    width: '100%',
  },
  button: {
    minWidth: 80,
  },
  selectInput: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey[500],
      width: 300,
    },
  },
}));

const schema = t.type({
  employeeId: requiredStringField(strings.employeeId),
  lastName: requiredStringField(strings.lastName),
  firstName: requiredStringField(strings.firstName),
  role: requiredStringField(strings.role),
  email: requiredStringField(strings.email),
});

const searchType = t.type({
  id: h.number().cast(),
});

export const Staff: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(searchType);
  const action = useRef<'update' | 'delete'>('update');
  const staff = useStaff(search.id);
  const agencyList = useAgencyList();
  const [shouldShowUpdateSuccess, setShouldUpdateSuccess] = useState(false);
  const staffData = staff.data;

  const history = useHistory();
  const { mutateAsync, isLoading, isError, error, isSuccess, reset } = usePatchStaff(search.id);

  const deleteStaff = useStaffDelete();

  const breadcrumb = useMemo(
    () => [
      ...getBreadcrumb(url('/admin/staffs/Staffs')),
      staffData ? new UserHelper(staffData).fullname : '',
    ],
    [staffData?.first_name, staffData?.last_name],
  );

  return (
    <Layout
      title={strings.title(staffData ? new UserHelper(staffData).fullname : '')}
      loading={staff.isLoading || agencyList.isLoading}
      breadcrumb={breadcrumb}
    >
      {() => (
        <div className={classes.container}>
          <SpinnerModal open={isLoading} />
          <FormTitle title={strings.title(staffData ? new UserHelper(staffData).fullname : '')} />
          <Formik
            initialValues={{
              avatar: staffData!.avatar_url || '',
              employeeId: staffData!.staff_id || '',
              firstName: staffData!.first_name || '',
              lastName: staffData!.last_name || '',
              role: mapRoleToRoleOptionValue(staffData?.role, staffData?.agency_id ?? 0) || '',
              email: staffData!.email || '',
            }}
            validate={createValidate(schema)}
            onSubmit={async (values) => {
              if (action.current === 'update') {
                const resolvedValues = {
                  staff_id: values.employeeId,
                  first_name: values.firstName,
                  last_name: values.lastName,
                  role: mapRoleOptionToRole(values.role),
                  agency_id: mapRoleOptionToAgencyId(values.role),
                };
                await mutateAsync(resolvedValues);
                setShouldUpdateSuccess(true);
                setTimeout(() => setShouldUpdateSuccess(false), 3000);
              } else if (action.current === 'delete') {
                await deleteStaff.mutateAsync(search.id);
                history.goBack();
              }
            }}
          >
            <Form className={classes.formBodyContainer}>
              <Alert open={isError} severity="error" onClose={reset}>
                {error instanceof FormError && error.getDisplayMessage()}
              </Alert>
              <Alert
                severity="success"
                open={isSuccess && shouldShowUpdateSuccess}
                onClose={() => {
                  setShouldUpdateSuccess(false);
                }}
              >
                {strings.updateSuccess(staffData ? new UserHelper(staffData).fullname : '-')}
              </Alert>
              <div className={clsx(classes.formBody)}>
                <AvatarInput name="avatar" imageWidth={120} imageHeight={120} round disabled />
                <TextInput
                  label={`${strings.employeeId}:`}
                  name="employeeId"
                  type="text"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <TextInput
                  label={`${strings.lastName}:`}
                  name="lastName"
                  type="text"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <TextInput
                  label={`${strings.firstName}:`}
                  name="firstName"
                  type="text"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <TextInput
                  label={`${strings.role}:`}
                  name="role"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                  choose
                  options={getRoleOptions(agencyList.data ?? [])}
                />
                <TextInput
                  label={`${strings.email}:`}
                  name="email"
                  type="text"
                  disabled
                  autoComplete="email"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <VerticalSpace />
                <div className={classes.rowContainer}>
                  <Button
                    text={strings.update!}
                    color="primary"
                    className={classes.button}
                    shape="round"
                    type="submit"
                    onClick={() => {
                      action.current = 'update';
                    }}
                  />
                  <HorizontalSpace width={14} />
                  <Button
                    text={strings.delete!}
                    color="primary"
                    className={classes.button}
                    shape="round"
                    type="submit"
                    onClick={() => {
                      action.current = 'delete';
                    }}
                  />
                </div>
              </div>
            </Form>
          </Formik>
        </div>
      )}
    </Layout>
  );
};

export const meta: PageMeta = {};
